import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Image, Badge, Pagination, Form, Row, Col, Modal, Spinner, Container, Tabs, Tab } from 'react-bootstrap';
import axiosInstance from '../../../utils/axiosConfig';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ApprovalRequestsTable = () => {
    const [offers, setOffers] = useState([]);
    const [news, setNews] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [termsConditions, setTermsConditions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemType, setSelectedItemType] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [comment, setComment] = useState('');
    const [approvingItemId, setApprovingItemId] = useState(null);
    const [activeTab, setActiveTab] = useState('offers');
    const navigate = useNavigate();

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        await Promise.all([
            fetchOffers(),
            fetchNews(),
            fetchVehicles(),
            fetchTermsConditions()
        ]);
        setLoading(false);
    };

    const fetchOffers = async () => {
        try {
            const response = await axiosInstance.get('/offers');
            if (response.data && Array.isArray(response.data.data)) {
                setOffers(response.data.data);
            }
        } catch (error) {
            console.error('Error loading offers:', error);
        }
    };

    const fetchNews = async () => {
        try {
            const response = await axiosInstance.get('/news');
            if (response.data && Array.isArray(response.data.data)) {
                setNews(response.data.data);
            }
        } catch (error) {
            console.error('Error loading news:', error);
        }
    };

    const fetchVehicles = async () => {
        try {
            const response = await axiosInstance.get('/vehicles');
            const data = response.data.data || response.data || [];
            if (Array.isArray(data)) {
                setVehicles(data);
            }
        } catch (error) {
            console.error('Error loading vehicles:', error);
        }
    };

    const fetchTermsConditions = async () => {
        try {
            const response = await axiosInstance.get('/terms-conditions');
            const data = response.data.data || response.data || [];
            if (Array.isArray(data)) {
                setTermsConditions(data);
            }
        } catch (error) {
            console.error('Error loading terms & conditions:', error);
        }
    };

    const getStatusBadge = (status) => {
        const statusMapping = {
            approved: { text: 'Approved & Published', variant: 'success' },
            rejected_for_edit: { text: 'Needs Revision', variant: 'warning' },
            rejected: { text: 'Rejected', variant: 'danger' },
            manager_approved: { text: 'Approved by Manager', variant: 'info' },
            pending: { text: 'In Review', variant: 'secondary' }
        };
        const { text, variant } = statusMapping[status] || { text: status, variant: 'dark' };
        return <Badge bg={variant}>{text}</Badge>;
    };

    const isExpired = (endDate) => {
        const today = new Date();
        const offerEndDate = new Date(endDate);
        return offerEndDate < today;
    };

    const getApproveEndpoint = (itemId, itemType) => {
        const endpoints = {
            offer: `/offers/${itemId}/approve`,
            news: `/news/${itemId}/approve`,
            vehicle: `/admin/vehicles/${itemId}/approve`,
            termsConditions: `/terms-conditions/${itemId}/approve`
        };
        return endpoints[itemType];
    };

    const getRejectEndpoint = (itemId, itemType) => {
        const endpoints = {
            offer: `/offers/${itemId}/reject`,
            news: `/news/${itemId}/reject`,
            vehicle: `/admin/vehicles/${itemId}/reject`,
            termsConditions: `/terms-conditions/${itemId}/reject`
        };
        return endpoints[itemType];
    };

    const getItemTypeName = (itemType) => {
        const names = {
            offer: 'Offer',
            news: 'News',
            vehicle: 'Vehicle',
            termsConditions: 'Terms & Conditions'
        };
        return names[itemType] || itemType;
    };

    const handleApprove = async (itemId, itemType) => {
        setApprovingItemId(itemId);
        try {
            const endpoint = getApproveEndpoint(itemId, itemType);
            const response = await axiosInstance.post(endpoint);

            const responseMessage = response.data.message;

            if (responseMessage && responseMessage.includes('already approved by manager')) {
                toast.error(`This ${getItemTypeName(itemType)} is already approved by manager and cannot be approved again.`);
                return;
            }

            if (responseMessage && responseMessage.includes('already approved')) {
                toast.error(`This ${getItemTypeName(itemType)} is already approved and published.`);
                return;
            }

            toast.success(`${getItemTypeName(itemType)} has been approved successfully!`);
            updateItemStatus(itemId, itemType, 'approved');
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                const errorMessage = error.response.data.message;

                if (errorMessage.includes('already approved by manager')) {
                    toast.error(`This ${getItemTypeName(itemType)} is already approved by manager and cannot be approved again.`);
                } else if (errorMessage.includes('already approved')) {
                    toast.error(`This ${getItemTypeName(itemType)} is already approved and published.`);
                } else {
                    toast.error(errorMessage);
                }
            } else {
                toast.error(`Failed to approve ${getItemTypeName(itemType)}. Please try again.`);
            }
        } finally {
            setApprovingItemId(null);
        }
    };

    const handleReject = async () => {
        if (comment.length < 10) {
            toast.error("Rejection comment must be at least 10 characters.");
            return;
        }
        try {
            const endpoint = getRejectEndpoint(selectedItem, selectedItemType);
            await axiosInstance.post(endpoint, {
                comment,
                rejection_type: rejectionReason
            });

            toast.success(`${getItemTypeName(selectedItemType)} has been rejected`);
            updateItemStatus(selectedItem, selectedItemType, rejectionReason);
            closeRejectModal();
        } catch (error) {
            toast.error(`Failed to reject ${getItemTypeName(selectedItemType)}`);
        }
    };

    const updateItemStatus = (itemId, itemType, newStatus) => {
        const updateFunctions = {
            offer: () => setOffers(prev => prev.map(item => item.id === itemId ? { ...item, status: newStatus } : item)),
            news: () => setNews(prev => prev.map(item => item.id === itemId ? { ...item, status: newStatus } : item)),
            vehicle: () => setVehicles(prev => prev.map(item => item.id === itemId ? { ...item, status: newStatus } : item)),
            termsConditions: () => setTermsConditions(prev => prev.map(item => item.id === itemId ? { ...item, status: newStatus } : item))
        };
        updateFunctions[itemType]?.();
    };

    const openRejectModal = (itemId, itemType) => {
        setSelectedItem(itemId);
        setSelectedItemType(itemType);
        setShowRejectModal(true);
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setRejectionReason('');
        setComment('');
    };

    const handleViewOffer = (id) => navigate(`/offers/view/${id}`);
    const handleViewNews = (id) => navigate(`/cms/blog-management/view/${id}`);
    const handleViewVehicle = (id) => navigate(`/vehicle-management/view/${id}`);

    const getCurrentData = () => {
        switch (activeTab) {
            case 'offers': return offers;
            case 'news': return news;
            case 'vehicles': return vehicles;
            case 'termsConditions': return termsConditions;
            default: return [];
        }
    };

    const filterData = (data) => {
        return data.filter((item) => {
            let nameEn = '', nameAr = '';

            switch (activeTab) {
                case 'offers':
                    nameEn = item.offer_name_en || '';
                    nameAr = item.offer_name_ar || '';
                    break;
                case 'news':
                    nameEn = item.title_en || '';
                    nameAr = item.title_ar || '';
                    break;
                case 'vehicles':
                    nameEn = item.vehicle_id || item.slug || '';
                    nameAr = item.name_ar || '';
                    break;
                case 'termsConditions':
                    nameEn = item.title_en || '';
                    nameAr = item.title_ar || '';
                    break;
                default:
                    break;
            }

            const matchesSearch = nameEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
                nameAr.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = statusFilter ? item.status === statusFilter : true;

            return matchesSearch && matchesStatus;
        });
    };

    const currentData = getCurrentData();
    const filteredData = filterData(currentData);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const renderActionButtons = (item, itemType, canExpire = false) => {
        const expired = canExpire && item.offer_end_date && isExpired(item.offer_end_date);

        return (
            <>
                {!expired && (
                    <>
                        <Button
                            variant="success"
                            size="sm"
                            className="me-1"
                            onClick={() => handleApprove(item.id, itemType)}
                            disabled={item.status === 'approved' || approvingItemId === item.id}
                        >
                            {approvingItemId === item.id ? <Spinner as="span" animation="border" size="sm" /> : "Approve"}
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => openRejectModal(item.id, itemType)}
                            disabled={item.status === 'approved'}
                        >
                            Reject
                        </Button>
                    </>
                )}
            </>
        );
    };

    const renderOffersTable = () => (
        <Table responsive striped bordered hover className="text-center">
            <thead>
                <tr>
                    <th>#</th>
                    <th>ID</th>
                    <th>Banner</th>
                    <th>Name (EN)</th>
                    <th>Name (AR)</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {currentItems.length === 0 ? (
                    <tr><td colSpan="9" className="text-center">No offers available</td></tr>
                ) : (
                    currentItems.map((offer, index) => (
                        <tr key={offer.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>{offer.id}</td>
                            <td>
                                {offer.offer_banner && (
                                    <Image src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/storage/${offer.offer_banner}`} alt="Banner" width={60} height={60} rounded />
                                )}
                            </td>
                            <td>{offer.offer_name_en}</td>
                            <td>{offer.offer_name_ar}</td>
                            <td>{offer.offer_start_date}</td>
                            <td>{offer.offer_end_date}</td>
                            <td>{getStatusBadge(offer.status)}</td>
                            <td>
                                <Button variant="info" size="sm" className="me-1" onClick={() => handleViewOffer(offer.id)}>View</Button>
                                {renderActionButtons(offer, 'offer', true)}
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </Table>
    );

    const renderNewsTable = () => (
        <Table responsive striped bordered hover className="text-center">
            <thead>
                <tr>
                    <th>#</th>
                    <th>ID</th>
                    <th>Image</th>
                    <th>Title (EN)</th>
                    <th>Title (AR)</th>
                    <th>Created By</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {currentItems.length === 0 ? (
                    <tr><td colSpan="8" className="text-center">No news available</td></tr>
                ) : (
                    currentItems.map((newsItem, index) => (
                        <tr key={newsItem.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>{newsItem.id}</td>
                            <td>
                                {newsItem.featured_image && (
                                    <Image src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/storage/${newsItem.featured_image}`} alt="Image" width={60} height={60} rounded />
                                )}
                            </td>
                            <td>{newsItem.title_en}</td>
                            <td>{newsItem.title_ar}</td>
                            <td>{newsItem.creator?.name || 'Unknown'}</td>
                            <td>{getStatusBadge(newsItem.status)}</td>
                            <td>
                                <Button variant="info" size="sm" className="me-1" onClick={() => handleViewNews(newsItem.id)}>View</Button>
                                {renderActionButtons(newsItem, 'news')}
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </Table>
    );

    const renderVehiclesTable = () => (
        <Table responsive striped bordered hover className="text-center">
            <thead>
                <tr>
                    <th>#</th>
                    <th>ID</th>
                    <th>Vehicle ID</th>
                    <th>Slug</th>
                    <th>Created By</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {currentItems.length === 0 ? (
                    <tr><td colSpan="7" className="text-center">No vehicles available</td></tr>
                ) : (
                    currentItems.map((vehicle, index) => (
                        <tr key={vehicle.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>{vehicle.id}</td>
                            <td>{vehicle.vehicle_id || '-'}</td>
                            <td>{vehicle.slug || '-'}</td>
                            <td>{vehicle.creator?.name || 'N/A'}</td>
                            <td>{getStatusBadge(vehicle.status)}</td>
                            <td>
                                <Button variant="info" size="sm" className="me-1" onClick={() => handleViewVehicle(vehicle.id)}>View</Button>
                                {renderActionButtons(vehicle, 'vehicle')}
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </Table>
    );

    const renderTermsConditionsTable = () => (
        <Table responsive striped bordered hover className="text-center">
            <thead>
                <tr>
                    <th>#</th>
                    <th>ID</th>
                    <th>Title (EN)</th>
                    <th>Title (AR)</th>
                    <th>Active</th>
                    <th>Created By</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {currentItems.length === 0 ? (
                    <tr><td colSpan="8" className="text-center">No terms & conditions available</td></tr>
                ) : (
                    currentItems.map((item, index) => (
                        <tr key={item.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>{item.id}</td>
                            <td>{item.title_en}</td>
                            <td>{item.title_ar}</td>
                            <td>
                                <Badge bg={item.is_active ? 'success' : 'secondary'}>
                                    {item.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            </td>
                            <td>{item.creator?.name || 'N/A'}</td>
                            <td>{getStatusBadge(item.status)}</td>
                            <td>
                                {renderActionButtons(item, 'termsConditions')}
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </Table>
    );

    const renderFilters = () => (
        <Form className="mb-3">
            <Row>
                <Col md={6}>
                    <Form.Control
                        type="search"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input-brand-shadow"
                    />
                </Col>
                <Col md={4}>
                    <Form.Control
                        as="select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="input-brand-shadow"
                    >
                        <option value="">All Status</option>
                        <option value="approved">Approved & Published</option>
                        <option value="rejected_for_edit">Needs Revision</option>
                        <option value="rejected">Rejected</option>
                        <option value="manager_approved">Approved by Manager</option>
                        <option value="pending">In Review</option>
                    </Form.Control>
                </Col>
                <Col md={2} className="text-end">
                    <Button variant="secondary" onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('');
                    }}>Clear</Button>
                </Col>
            </Row>
        </Form>
    );

    const renderCurrentTable = () => {
        switch (activeTab) {
            case 'offers': return renderOffersTable();
            case 'news': return renderNewsTable();
            case 'vehicles': return renderVehiclesTable();
            case 'termsConditions': return renderTermsConditionsTable();
            default: return null;
        }
    };

    if (loading) return <div className="text-center mt-5">Loading approval requests...</div>;

    return (
        <Container fluid>
            <div className="table-container">
                <div className='approval-requests-page mt-5 mb-5'>
                    <h3>Approval Requests</h3>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => {
                        setActiveTab(k);
                        setCurrentPage(1);
                        setSearchTerm('');
                        setStatusFilter('');
                    }}
                    className="mb-3"
                >
                    <Tab eventKey="offers" title={`Offers (${offers.filter(o => o.status === 'pending').length})`}>
                        {renderFilters()}
                        <div className="table-brand-shadow">{renderCurrentTable()}</div>
                    </Tab>

                    <Tab eventKey="news" title={`News (${news.filter(n => n.status === 'pending').length})`}>
                        {renderFilters()}
                        <div className="table-brand-shadow">{renderCurrentTable()}</div>
                    </Tab>

                    <Tab eventKey="vehicles" title={`Vehicles (${vehicles.filter(v => v.status === 'pending').length})`}>
                        {renderFilters()}
                        <div className="table-brand-shadow">{renderCurrentTable()}</div>
                    </Tab>

                    <Tab eventKey="termsConditions" title={`T&C (${termsConditions.filter(t => t.status === 'pending').length})`}>
                        {renderFilters()}
                        <div className="table-brand-shadow">{renderCurrentTable()}</div>
                    </Tab>
                </Tabs>

                <Pagination className="justify-content-end">
                    {[...Array(totalPages)].map((_, i) => (
                        <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => handlePageChange(i + 1)}
                        >
                            {i + 1}
                        </Pagination.Item>
                    ))}
                </Pagination>

                <Modal show={showRejectModal} onHide={closeRejectModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Reject {getItemTypeName(selectedItemType)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group controlId="rejectionReason">
                                <Form.Label>Reason for Rejection</Form.Label>
                                <Form.Control as="select" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} required>
                                    <option value="">Select reason</option>
                                    <option value="rejected_for_edit">Reject for Edit</option>
                                    <option value="rejected">Permanently Reject</option>
                                </Form.Control>
                            </Form.Group>
                            <Form.Group controlId="comment" className="mt-3">
                                <Form.Label>Rejection Comment</Form.Label>
                                <Form.Control as="textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} required />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeRejectModal}>Cancel</Button>
                        <Button variant="danger" onClick={handleReject}>Reject</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Container>
    );
};

export default ApprovalRequestsTable;
